import { default as cx } from "classnames";
import { format, isSameDay } from "date-fns";
import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { formatPermitHours } from "@/lib/utils";
import { Car, Euro } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type {
  CalendarItem,
  Expense,
  Mileage,
  Presence,
} from "../../types/presence";
import { useUserId } from "../../hooks/useUserId";
import { useVehiclePlates } from "../../hooks/data/use-settings-data";
import Typography from "../Typography";
import AbsenceModal from "./AbsenceModal";
import ExpenseForm from "./ExpenseForm";
import MileageForm from "./MileageForm";

/*
    empty calendar box for dispay passt month days
*/
type DefinitionProps = {
  disable?: boolean | false;
  day: Date;
  items?: CalendarItem[];
};

export default function CalendarBox({
  disable = false,
  day,
  items,
}: DefinitionProps) {
  const navigate = useNavigate();
  const userId = useUserId();
  const { data: vehicles = [] } = useVehiclePlates(userId);

  const [showPresenceForm, setShowPresenceForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showMileageForm, setShowMileageForm] = useState(false);

  const [selectedPresence, setSelectedPresence] = useState<
    Presence | undefined
  >(undefined);
  const [selectedExpense, setSelectedExpense] = useState<Expense | undefined>(
    undefined,
  );
  const [selectedMileage, setSelectedMileage] = useState<Mileage | undefined>(
    undefined,
  );

  const handleClosePresenceForm = useCallback(() => {
    setShowPresenceForm(false);
    setSelectedPresence(undefined);
  }, []);

  const handleCloseExpenseForm = useCallback(() => {
    setShowExpenseForm(false);
    setSelectedExpense(undefined);
  }, []);

  const handleCloseMileageForm = useCallback(() => {
    setShowMileageForm(false);
    setSelectedMileage(undefined);
  }, []);

  const isOnFerieOrMalattia = items?.some(
    (item) =>
      "reason" in item &&
      (item.reason === "ferie" ||
        item.reason === "festività" ||
        item.reason === "chiusura ufficio" ||
        item.reason === "malattia" ||
        item.reason === "maternità"),
  ) ?? false;

  const handleAddAbsence = () => {
    const existingPresence = items?.find((item) => "reason" in item) as Presence | undefined;
    setSelectedPresence(existingPresence);
    setShowPresenceForm(true);
  };

  const handleAddExpense = () => {
    const existingExpense = items?.find(
      (item): item is Expense => "type" in item && item.type === "expense",
    );
    setSelectedExpense(existingExpense);
    setShowExpenseForm(true);
  };

  const handleAddMileage = () => {
    if (vehicles.length === 0) {
      toast.warning("Nessun veicolo registrato. Aggiungine uno per inserire rimborsi kilometrici.", {
        action: {
          label: "Vai alle impostazioni",
          onClick: () => navigate("/settings"),
        },
      });
      return;
    }
    const existingMileage = items?.find(
      (item): item is Mileage => "type" in item && item.type === "mileage",
    );
    setSelectedMileage(existingMileage);
    setShowMileageForm(true);
  };

  const handleEditItem = (item: CalendarItem) => {
    if ("reason" in item) {
      setSelectedPresence(item as Presence);
      setShowPresenceForm(true);
    } else if ("type" in item && item.type === "expense") {
      setSelectedExpense(item as Expense);
      setShowExpenseForm(true);
    } else if ("type" in item && item.type === "mileage") {
      setSelectedMileage(item as Mileage);
      setShowMileageForm(true);
    }
  };

  return (
    <>
      {showPresenceForm &&
        createPortal(
          <AbsenceModal
            date={day}
            data={selectedPresence}
            close={handleClosePresenceForm}
          />,
          document.body,
        )}
      {showExpenseForm &&
        createPortal(
          <ExpenseForm
            date={day}
            data={selectedExpense}
            close={handleCloseExpenseForm}
          />,
          document.body,
        )}
      {showMileageForm &&
        createPortal(
          <MileageForm
            date={day}
            data={selectedMileage}
            close={handleCloseMileageForm}
          />,
          document.body,
        )}
      <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disable}>
      <button
        className={`CalendarBox ${disable ? "CalendarBox__disabled" : ""}`}
      >
        {isSameDay(day, new Date()) && (
          <div className="CalendarBox__same-day-indicator"></div>
        )}
        <Typography variant="h5" className="CalendarBox__day-number">
          {format(day, "d")}
        </Typography>
        <div className="flex flex-col gap-1 px-1 overflow-y-auto max-h-[calc(100%-2.5rem)]">
          {/* Mobile: expense/mileage icons top-right */}
          {(() => {
            const hasExpense = items?.some((item) => "type" in item && item.type === "expense");
            const hasMileage = items?.some((item) => "type" in item && item.type === "mileage");
            if (!hasExpense && !hasMileage) return null;
            return (
              <div className="CalendarBox__icons-mobile">
                {hasExpense && (
                  <div className="CalendarBox__icons-mobile-expense">
                    <Euro size={10} />
                  </div>
                )}
                {hasMileage && (
                  <div className="CalendarBox__icons-mobile-mileage">
                    <Car size={10} />
                  </div>
                )}
              </div>
            );
          })()}
          {items?.map((item) => {
            if ("reason" in item) {
              const p = item as Presence;
              return (
                <div
                  key={p.sk}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditItem(p);
                  }}
                >
                  <>
                    {/* Desktop */}
                    <Typography
                      variant="body2"
                      className={cx(
                        "CalendarBox__indicatore-desktop  CalendarBox__presence-reason",
                        {
                          "CalendarBox__indicatore-desktop-ferie":
                            p.reason === "ferie",
                          "CalendarBox__indicatore-desktop-festività":
                            p.reason === "festività",
                          "CalendarBox__indicatore-desktop-chiusura-ufficio":
                            p.reason === "chiusura ufficio",
                          "CalendarBox__indicatore-desktop-permesso":
                            p.reason === "permesso",
                          "CalendarBox__indicatore-desktop-malattia":
                            p.reason === "malattia",
                          "CalendarBox__indicatore-desktop-maternità":
                            p.reason === "maternità",
                          "CalendarBox__indicatore-desktop-104":
                            p.reason === "104",
                        },
                      )}
                    >
                      {p.reason === "104" ? "Permesso 104" : p.reason}
                      {(p.reason === "permesso" || p.reason === "104") && p.hours
                        ? ` - ${formatPermitHours(p.hours)}`
                        : ""}
                    </Typography>
                    {/* Mobile */}
                    <div
                      className={cx(
                        "CalendarBox__indicatore-mobile  CalendarBox__presence-reason",
                        {
                          "CalendarBox__indicatore-mobile-ferie":
                            p.reason === "ferie",
                          "CalendarBox__indicatore-mobile-festività":
                            p.reason === "festività",
                          "CalendarBox__indicatore-mobile-chiusura-ufficio":
                            p.reason === "chiusura ufficio",
                          "CalendarBox__indicatore-mobile-permesso":
                            p.reason === "permesso",
                          "CalendarBox__indicatore-mobile-malattia":
                            p.reason === "malattia",
                          "CalendarBox__indicatore-mobile-maternità":
                            p.reason === "maternità",
                          "CalendarBox__indicatore-mobile-104":
                            p.reason === "104",
                        },
                      )}
                    ></div>
                  </>
                </div>
              );
            } else if ("type" in item && item.type === "expense") {
              const e = item as Expense;
              return (
                <div
                  key={e.sk}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    handleEditItem(item);
                  }}
                >
                  <Typography
                    variant="body2"
                    className="CalendarBox__indicatore CalendarBox__indicatore-expense"
                  >
                    Spesa: {e.cost}€
                  </Typography>
                </div>
              );
            } else if ("type" in item && item.type === "mileage") {
              const m = item as Mileage;
              return (
                <div
                  key={m.sk}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    handleEditItem(item);
                  }}
                >
                  <Typography
                    variant="body2"
                    className="CalendarBox__indicatore CalendarBox__indicatore-mileage"
                  >
                    {m.distance} km → {m.refound.toFixed(2)}€
                  </Typography>
                </div>
              );
            }
            return null;
          })}
        </div>
      </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onSelect={handleAddAbsence}>
          Aggiungi/Modifica Assenza
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleAddExpense} disabled={isOnFerieOrMalattia}>
          Aggiungi/Modifica Documento Spesa
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleAddMileage} disabled={isOnFerieOrMalattia}>
          Aggiungi/Modifica Rimborso Kilometrico
        </DropdownMenuItem>
      </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
